import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '@/contexts/ChatContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import {
  Phone,
  PhoneOff,
  Video,
  VideoOff,
  Mic,
  MicOff,
  X,
  Maximize2,
  Minimize2
} from 'lucide-react';

const VideoCall = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  
  const {
    activeCall,
    incomingCall,
    localStream,
    remoteStream,
    callStatus,
    answerCall,
    rejectCall,
    endCall,
    toggleVideo,
    toggleAudio,
  } = useChat();

  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const callTimerRef = useRef(null);

  // Set local video stream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Set remote video stream
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Call duration timer
  useEffect(() => {
    if (callStatus === 'connected') {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callStatus]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleToggleVideo = () => {
    const newState = !videoEnabled;
    setVideoEnabled(newState);
    toggleVideo(newState);
  };

  const handleToggleAudio = () => {
    const newState = !audioEnabled;
    setAudioEnabled(newState);
    toggleAudio(newState);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Incoming call UI
  if (incomingCall && callStatus === 'ringing') {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]" data-testid="incoming-call">
        <Card className="w-96 p-8 text-center bg-slate-800 border-slate-700">
          <div className="mb-6">
            <div className="relative inline-block">
              <Avatar className="h-24 w-24 mx-auto ring-4 ring-teal-500 ring-offset-4 ring-offset-slate-800">
                <AvatarImage src={incomingCall.caller.image} />
                <AvatarFallback className="text-2xl bg-teal-600 text-white">
                  {incomingCall.caller.name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="absolute -bottom-1 -right-1 p-2 bg-teal-500 rounded-full animate-pulse">
                {incomingCall.type === 'video' ? (
                  <Video className="h-4 w-4 text-white" />
                ) : (
                  <Phone className="h-4 w-4 text-white" />
                )}
              </span>
            </div>
          </div>
          
          <h3 className="text-xl font-bold text-white mb-2">
            {incomingCall.caller.name}
          </h3>
          <p className="text-slate-400 mb-8">
            {incomingCall.type === 'video' 
              ? (lang === 'ar' ? 'مكالمة فيديو واردة...' : 'Incoming video call...')
              : (lang === 'ar' ? 'مكالمة صوتية واردة...' : 'Incoming voice call...')
            }
          </p>
          
          <div className="flex justify-center gap-6">
            <Button
              size="lg"
              variant="destructive"
              className="h-16 w-16 rounded-full"
              onClick={rejectCall}
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
            <Button
              size="lg"
              className="h-16 w-16 rounded-full bg-green-500 hover:bg-green-600"
              onClick={answerCall}
            >
              <Phone className="h-6 w-6" />
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Active call UI
  if (activeCall && (callStatus === 'calling' || callStatus === 'connected')) {
    const isVideoCall = activeCall.type === 'video';

    return (
      <div 
        className={`fixed ${isFullscreen ? 'inset-0' : 'bottom-4 right-4 w-96 h-[500px]'} bg-slate-900 rounded-lg overflow-hidden z-[100] shadow-2xl`}
        data-testid="active-call"
      >
        {/* Remote video (full size) */}
        <div className="absolute inset-0 bg-slate-800">
          {isVideoCall && remoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Avatar className="h-32 w-32">
                <AvatarImage src={activeCall.participant?.image} />
                <AvatarFallback className="text-4xl bg-teal-600 text-white">
                  {activeCall.participant?.name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
            </div>
          )}
        </div>

        {/* Header */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={activeCall.participant?.image} />
                <AvatarFallback className="bg-teal-600 text-white">
                  {activeCall.participant?.name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-white font-medium">{activeCall.participant?.name}</p>
                <p className="text-white/70 text-sm">
                  {callStatus === 'calling' 
                    ? (lang === 'ar' ? 'جاري الاتصال...' : 'Calling...')
                    : formatDuration(callDuration)
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                className="text-white hover:bg-white/20"
                onClick={handleFullscreen}
              >
                {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Local video (picture-in-picture) */}
        {isVideoCall && localStream && (
          <div className="absolute bottom-24 right-4 w-32 h-44 bg-slate-700 rounded-lg overflow-hidden shadow-lg border-2 border-slate-600">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover mirror"
              style={{ transform: 'scaleX(-1)' }}
            />
            {!videoEnabled && (
              <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
                <VideoOff className="h-8 w-8 text-slate-400" />
              </div>
            )}
          </div>
        )}

        {/* Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex items-center justify-center gap-4">
            <Button
              size="lg"
              variant={audioEnabled ? 'secondary' : 'destructive'}
              className="h-14 w-14 rounded-full"
              onClick={handleToggleAudio}
            >
              {audioEnabled ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
            </Button>
            
            {isVideoCall && (
              <Button
                size="lg"
                variant={videoEnabled ? 'secondary' : 'destructive'}
                className="h-14 w-14 rounded-full"
                onClick={handleToggleVideo}
              >
                {videoEnabled ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
              </Button>
            )}
            
            <Button
              size="lg"
              variant="destructive"
              className="h-14 w-14 rounded-full"
              onClick={endCall}
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default VideoCall;
